import {
  skipRenewVisaDocumentStepGroupApi,
  useGetRenewVisaBasicInformationDocumentGroupInfo,
  useGetRenewVisaDocumentGroupInfo,
} from "@/api/services/main/renew-visa";
import { IconTriangleDown } from "@/assets/icons/IconTriangleDown";
import { Button } from "@/components/elements/Button";
import { Checkbox } from "@/components/elements/Checkbox";
import { LoadingPage } from "@/components/elements/Loading/Loading";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { ConfirmResetRenewVisaDocumentGroupModal } from "@/features/renew-visa/components/ConfirmResetRenewVisaDocumentGroupModal";
import { RenewVisaAfterSubmitApplicationResultStep } from "@/features/renew-visa/components/present/RenewVisaAfterSubmitApplicationResultStep";
import { RenewVisaAfterSubmitApplicationStep } from "@/features/renew-visa/components/present/RenewVisaAfterSubmitApplicationStep";
import { RenewVisaApplicationStep } from "@/features/renew-visa/components/present/RenewVisaApplicationStep";
import { RenewVisaBasicInfoStep } from "@/features/renew-visa/components/present/RenewVisaBasicInfoStep";
import { RenewVisaOnlineSubmitApplicationStep } from "@/features/renew-visa/components/present/RenewVisaOnlineSubmitApplicationStep";
import { RenewVisaResidenceCardInfoStep } from "@/features/renew-visa/components/present/RenewVisaResidenceCardInfoStep";
import { RenewVisaUpdateDocumentNoteStep } from "@/features/renew-visa/components/present/RenewVisaUpdateDocumentNoteStep";
import { findRenewVisaDocumentStepGroup } from "@/features/renew-visa/utils/findDocumentStepGroup";
import { useVisible } from "@/hooks/useVisible";
import { text } from "@/theme/colors";
import { DOCUMENT_STEP_KEYS, RENEW_VISA_DOCUMENT_STATUS, RENEW_VISA_DOCUMENT_STEP_STATUS } from "@/types/enum";
import { showError } from "@/utils/error";
import { toNumber } from "@/utils/number";
import { Box, Stack } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

export const RenewVisaPresentPage = () => {
  const queryClient = useQueryClient();
  const confirmResetRenewVisaDocumentGroupModal = useVisible();

  const { data: renewVisaDocumentGroup, isLoading: isLoadingRenewVisaDocumentGroup } = useGetRenewVisaDocumentGroupInfo();

  const basicInfoStepGroupData = findRenewVisaDocumentStepGroup(renewVisaDocumentGroup, DOCUMENT_STEP_KEYS.RENEW_VISA_GROUP_BASIC_INFORMATION);
  const applicationStepGroupData = findRenewVisaDocumentStepGroup(renewVisaDocumentGroup, DOCUMENT_STEP_KEYS.RENEW_VISA_GROUP_CREATE_DOCUMENT);
  const onlineSubmitApplicationStepGroupData = findRenewVisaDocumentStepGroup(renewVisaDocumentGroup, DOCUMENT_STEP_KEYS.RENEW_VISA_GROUP_SUBMIT);
  const afterSubmitApplicationStepGroupData = findRenewVisaDocumentStepGroup(
    renewVisaDocumentGroup,
    DOCUMENT_STEP_KEYS.RENEW_VISA_GROUP_AFTER_SUBMIT_APPLICATION,
  );
  const afterSubmitApplicationResultStepGroupData = findRenewVisaDocumentStepGroup(
    renewVisaDocumentGroup,
    DOCUMENT_STEP_KEYS.RENEW_VISA_GROUP_AFTER_SUBMIT_APPLICATION_RESULT,
  );
  const residenceCardInfoStepGroupData = findRenewVisaDocumentStepGroup(
    renewVisaDocumentGroup,
    DOCUMENT_STEP_KEYS.RENEW_VISA_GROUP_UPDATE_RESIDENCE_CARD,
  );

  const { data: basicInfoData, isLoading: isLoadingBasicInfo } = useGetRenewVisaBasicInformationDocumentGroupInfo({
    renewVisaDocumentStepGroupId: toNumber(basicInfoStepGroupData?.id),
  });

  const isFinishRenewVisaDocumentGroup = useMemo(
    () => !!renewVisaDocumentGroup?.status && renewVisaDocumentGroup?.status === RENEW_VISA_DOCUMENT_STATUS.COMPLETE,
    [renewVisaDocumentGroup],
  );

  const [isSkipStep, setIsSkipStep] = useState<boolean>(false);

  const handleSkipStep = async (checked: boolean) => {
    setIsSkipStep(checked);
    try {
      if (!renewVisaDocumentGroup) return;
      await skipRenewVisaDocumentStepGroupApi({ renewVisaDocumentGroupId: toNumber(renewVisaDocumentGroup.id), isSkipStep: checked });
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RENEW_VISA_DOCUMENTS.FETCH_GROUP_DETAIL] });
    } catch (error) {
      showError(error);
    }
  };

  useEffect(() => {
    setIsSkipStep(!!renewVisaDocumentGroup?.isSkipStep);
  }, [renewVisaDocumentGroup]);

  const isLoading = useMemo(() => isLoadingRenewVisaDocumentGroup || isLoadingBasicInfo, [isLoadingRenewVisaDocumentGroup, isLoadingBasicInfo]);

  if (isLoading) return <LoadingPage />;

  return (
    <>
      <Box p="24px">
        <Stack flexDirection="row" alignItems="center" mb={3} justifyContent="space-between">
          <Stack flexDirection="row" gap={1}></Stack>
          <Stack flexDirection="row" alignItems="center" gap={4}>
            <Checkbox
              disabled={isFinishRenewVisaDocumentGroup}
              checked={isSkipStep}
              label={!!basicInfoData ? "ステップ1～3までスキップする" : "ステップ1～2までスキップする"}
              labelColor={text.secondary}
              onChange={(e, checked) => handleSkipStep(checked)}
            />
            {renewVisaDocumentGroup?.hasReset && (
              <Button
                size="sm"
                variant="danger"
                onClick={() => {
                  confirmResetRenewVisaDocumentGroupModal.open();
                }}
              >
                申請を削除
              </Button>
            )}
          </Stack>
        </Stack>
        {!!basicInfoData && (
          <>
            {basicInfoStepGroupData && (
              <RenewVisaBasicInfoStep isSkipStep={isSkipStep} stepGroupData={basicInfoStepGroupData} readonly={isFinishRenewVisaDocumentGroup} />
            )}
            <Stack alignItems="center" my={3}>
              <IconTriangleDown color={text.disable} />
            </Stack>
          </>
        )}
        {applicationStepGroupData && (
          <RenewVisaApplicationStep
            isExistBasicInfoData={!!basicInfoData}
            isSkipStep={isSkipStep}
            stepGroupData={applicationStepGroupData}
            readonly={isFinishRenewVisaDocumentGroup}
          />
        )}
        <Stack alignItems="center" my={3}>
          <IconTriangleDown color={text.disable} />
        </Stack>
        {onlineSubmitApplicationStepGroupData && (
          <RenewVisaOnlineSubmitApplicationStep
            isExistBasicInfoData={!!basicInfoData}
            isSkipStep={isSkipStep}
            stepGroupData={onlineSubmitApplicationStepGroupData}
            readonly={applicationStepGroupData?.status !== RENEW_VISA_DOCUMENT_STEP_STATUS.COMPLETE || isFinishRenewVisaDocumentGroup}
          />
        )}
        <Stack alignItems="center" my={3}>
          <IconTriangleDown color={text.disable} />
        </Stack>
        {afterSubmitApplicationStepGroupData && (
          <RenewVisaAfterSubmitApplicationStep
            isExistBasicInfoData={!!basicInfoData}
            stepGroupData={afterSubmitApplicationStepGroupData}
            readonly={
              (!isSkipStep && onlineSubmitApplicationStepGroupData?.status !== RENEW_VISA_DOCUMENT_STEP_STATUS.COMPLETE) ||
              isFinishRenewVisaDocumentGroup
            }
          />
        )}
        <Stack alignItems="center" my={3}>
          <IconTriangleDown color={text.disable} />
        </Stack>
        {afterSubmitApplicationResultStepGroupData && (
          <RenewVisaAfterSubmitApplicationResultStep
            isExistBasicInfoData={!!basicInfoData}
            stepGroupData={afterSubmitApplicationResultStepGroupData}
            readonly={afterSubmitApplicationStepGroupData?.status !== RENEW_VISA_DOCUMENT_STEP_STATUS.COMPLETE || isFinishRenewVisaDocumentGroup}
          />
        )}
        <Stack alignItems="center" my={3}>
          <IconTriangleDown color={text.disable} />
        </Stack>
        {residenceCardInfoStepGroupData && (
          <RenewVisaResidenceCardInfoStep
            isExistBasicInfoData={!!basicInfoData}
            isFinishAfterSubmitApplicationResultStep={afterSubmitApplicationResultStepGroupData?.status === RENEW_VISA_DOCUMENT_STEP_STATUS.COMPLETE}
            stepGroupData={residenceCardInfoStepGroupData}
            readonly={
              afterSubmitApplicationResultStepGroupData?.status !== RENEW_VISA_DOCUMENT_STEP_STATUS.COMPLETE || isFinishRenewVisaDocumentGroup
            }
          />
        )}
        <RenewVisaUpdateDocumentNoteStep readonly={isFinishRenewVisaDocumentGroup} />
      </Box>
      <ConfirmResetRenewVisaDocumentGroupModal
        isOpen={confirmResetRenewVisaDocumentGroupModal.visible}
        onClose={() => confirmResetRenewVisaDocumentGroupModal.close()}
      />
    </>
  );
};
